import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {Plugin, PluginKey} from 'prosemirror-state';
import {Paper, ThemeProvider} from '@mui/material';
import theme from '../../../theme';
import {parseCitationRefAttrs} from '../core/schema';
import {CitationRefCard, openCitationRefSource} from '../components/CitationRefCard';
import {resolveFloatingPanelLayout} from '../components/floatingPanelPosition';

export const citationRefPopoverPluginKey = new PluginKey('citationRefPopover');

/**
 * Parse a `citationRef` mark's rendered attrs straight off its DOM element (rather than
 * re-deriving them from ProseMirror mark instances) so this plugin works the same way whether
 * the mark came from a live transaction or from HTML/collab round-trip. Delegates to the same
 * `parseCitationRefAttrs` the mark's own `parseDOM` rule uses, so the two can't drift apart.
 */
export function readCitationAttrsFromDom(el) {
    if (!el) return null;
    if (!el.getAttribute('data-citation-ref-key')) return null;
    return parseCitationRefAttrs(el);
}


function CitationRefFloatingPanel({state, onClose}) {
    const panelRef = useRef(null);
    const [layout, setLayout] = useState({visible: false, left: 0, top: 0});
    const anchorEl = state?.anchorEl || null;

    const updatePosition = useCallback(() => {
        if (!anchorEl) return;
        const panelWidth = panelRef.current?.offsetWidth || 320;
        const panelHeight = panelRef.current?.offsetHeight || 0;
        setLayout(resolveFloatingPanelLayout({anchorEl, panelWidth, panelHeight, viewDom: state?.viewDom || null}));
    }, [anchorEl, state]);

    useEffect(() => {
        if (!anchorEl) return undefined;
        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [anchorEl, updatePosition]);

    useEffect(() => {
        if (!state) return undefined;
        const onPointerDown = (event) => {
            const target = event.target;
            if (panelRef.current?.contains(target)) return;
            if (anchorEl?.contains?.(target)) return;
            onClose();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [anchorEl, onClose, state]);

    useEffect(() => {
        if (!state) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, state]);

    if (!state || !anchorEl) return null;

    return (
        <Paper
            ref={panelRef}
            elevation={6}
            onMouseLeave={() => {
                if (!state.pinned) onClose();
            }}
            sx={{
                position: 'fixed',
                left: layout.left,
                top: layout.top,
                visibility: layout.visible ? 'visible' : 'hidden',
                zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
                pointerEvents: layout.visible ? 'auto' : 'none',
                width: 320,
                maxWidth: '90vw',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <CitationRefCard
                attrs={state.attrs}
                onOpenSource={(attrs) => {
                    openCitationRefSource(attrs);
                    onClose();
                }}
            />
        </Paper>
    );
}

/**
 * Hover/click popover for in-document `citationRef` marks (MINSKY-16823, ticket 04). Mirrors
 * the Forecast panel preview's hover-preview + click-to-pin behavior (ticket 02) and reuses the
 * same "open data item" action (ticket 03) — implemented as a self-contained DOM-event-driven
 * plugin view (like `InlineInteractionPopoverView`'s wikidata hover handling) rather than the
 * caret-driven machinery in `inlineInteractionPopoverPlugin.jsx`, since citation marks are
 * plain highlighted text, not atom nodes with a meaningful "caret landed here" concept.
 */
class CitationRefPopoverView {
    constructor(view) {
        this.view = view;
        this.state = null;
        this.host = document.createElement('div');
        this.host.className = 'pm-citation-ref-popover-host';
        this.host.style.position = 'fixed';
        this.host.style.inset = '0';
        this.host.style.pointerEvents = 'none';
        this.host.style.overflow = 'visible';
        this.host.style.zIndex = '0';
        document.body.appendChild(this.host);
        this.root = createRoot(this.host);

        this.handleMouseOver = this.handleMouseOver.bind(this);
        this.handleMouseOut = this.handleMouseOut.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleClose = this.handleClose.bind(this);

        if (view?.dom) {
            view.dom.addEventListener('mouseover', this.handleMouseOver);
            view.dom.addEventListener('mouseout', this.handleMouseOut);
            view.dom.addEventListener('click', this.handleClick);
        }

        this.render();
    }

    setState(next) {
        this.state = next;
        this.render();
    }

    handleMouseOver(event) {
        if (this.state?.pinned) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        const el = target.closest('.citation-ref-mark');
        if (!el || !this.view?.dom?.contains(el)) return;
        const attrs = readCitationAttrsFromDom(el);
        if (!attrs) return;
        this.setState({anchorEl: el, attrs, pinned: false, viewDom: this.view?.dom || null});
    }

    handleMouseOut(event) {
        if (this.state?.pinned) return;
        const related = event.relatedTarget;
        if (related instanceof Element && related.closest('.citation-ref-mark')) return;
        if (!this.state) return;
        this.setState(null);
    }

    handleClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const el = target.closest('.citation-ref-mark');
        if (!el || !this.view?.dom?.contains(el)) return;
        const attrs = readCitationAttrsFromDom(el);
        if (!attrs) return;
        if (this.state?.pinned && this.state.attrs?.key === attrs.key && this.state.anchorEl === el) {
            this.setState(null);
            return;
        }
        this.setState({anchorEl: el, attrs, pinned: true, viewDom: this.view?.dom || null});
    }

    handleClose() {
        this.setState(null);
    }

    render() {
        if (this._destroyed) return;
        this.root.render(
            <ThemeProvider theme={theme}>
                <CitationRefFloatingPanel state={this.state} onClose={this.handleClose}/>
            </ThemeProvider>
        );
    }

    update(view) {
        this.view = view;
    }

    destroy() {
        this._destroyed = true;
        if (this.view?.dom) {
            this.view.dom.removeEventListener('mouseover', this.handleMouseOver);
            this.view.dom.removeEventListener('mouseout', this.handleMouseOut);
            this.view.dom.removeEventListener('click', this.handleClick);
        }
        const root = this.root;
        const host = this.host;
        queueMicrotask(() => {
            root.unmount();
            host.remove();
        });
    }
}

export function createCitationRefPopoverPlugin() {
    return new Plugin({
        key: citationRefPopoverPluginKey,
        view(view) {
            return new CitationRefPopoverView(view);
        },
    });
}
